/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useId } from 'react';
import { View } from '@az/starc-ui';
import cx from 'classnames';
import styles from './ratingItemStyles.module.scss';

export type RatingItemProps = {
  label: string;
  icon?: React.ReactNode;
  itemValue: number;
  ratingValue: number | null;
  selectedValue: number | null;
  hover: number;
  className?: string;
  inputClassName?: string;
  hoveredClassName?: string;
  disabledClassName?: string;
  selectedClassName?: string;
  iconClassName?: string;
  activeColor?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClick?: (event: React.MouseEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onMouseEnter?: (event: React.MouseEvent<HTMLInputElement>, value: number) => void;
  disabled?: boolean;
  name: string;
  id?: string;
  autoFocus?: boolean;
};

export const RatingItem = ({
  label,
  icon,
  disabled,
  itemValue,
  name,
  ratingValue,
  selectedValue,
  hover,
  className,
  inputClassName,
  hoveredClassName = '',
  disabledClassName = '',
  selectedClassName = '',
  iconClassName = '',
  autoFocus,
  onChange,
  onClick,
  onFocus,
  onBlur,
  onMouseEnter,
  id: propsId,
}: RatingItemProps) => {
  const id = useId();

  const isFilled = itemValue <= (ratingValue ?? 0);
  const isHovered = itemValue <= hover;
  const isChecked = itemValue === selectedValue;

  const IconComponent = icon || (
    <div
      className={cx(
        styles.ratingItemIcon,
        {
          [styles.empty]: !isFilled,
        },
        iconClassName,
      )}
    ></div>
  );

  return (
    <View
      align={'center'}
      justify={'center'}
      className={cx(styles.root, className)}
      attributes={{
        'data-testid': 'rating-item',
        onMouseEnter: (event: React.MouseEvent<HTMLInputElement>) => {
          onMouseEnter?.(event, itemValue);
        },
      }}
    >
      <input
        className={cx(styles.ratingRadioButton, inputClassName, {
          [selectedClassName]: isFilled,
          [hoveredClassName]: isHovered,
          [disabledClassName]: disabled,
        })}
        onChange={onChange}
        onClick={onClick}
        disabled={disabled}
        value={itemValue}
        id={propsId ?? id}
        type="radio"
        name={name}
        checked={isChecked}
        autoFocus={autoFocus}
        onFocus={onFocus}
        onBlur={onBlur}
      />
      <label htmlFor={propsId ?? id}>
        <span className={styles.star} aria-hidden>
          {IconComponent}
        </span>
        <span className={cx('sr-only', styles.a11label)} tabIndex={-1}>
          {label}
        </span>
      </label>
    </View>
  );
};
